// src/components/Contact.tsx
import React, { useState } from 'react';
import './Contact.css';
import { API_ENDPOINTS } from '../config/api';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    email: '',
    phone: '',
    grade: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{success: boolean; message: string} | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const response = await fetch(API_ENDPOINTS.ENROLL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: `Admission Inquiry for ${formData.student_name} - Grade ${formData.grade}`
        }),
      });

      if (response.ok) {
        setSubmitStatus({ 
          success: true, 
          message: 'Thank you for your interest! We will contact you soon with more details.' 
        });
        setFormData({
          student_name: '',
          parent_name: '',
          email: '',
          phone: '',
          grade: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setSubmitStatus({ 
        success: false, 
        message: 'Something went wrong. Please try again later.' 
      });
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <h2 className="section-title">Contact Us</h2>
        <p className="section-subtitle text-center">Get in touch with us for any inquiries or to schedule a visit</p>
        
        <div className="contact-content">
          {/* First Row: Form and Map */}
          <div className="row g-4 mb-4">
            {/* Enrollment Form Column */}
            <div className="col-lg-6">
              <div className="enrollment-form-container-contact h-100">
                <h3 className="form-title">Admission Enquiry</h3>
                {submitStatus && (
                  <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-error'}`}>
                    {submitStatus.message}
                  </div>
                )}
                <form className="enrollment-form" onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-group">
                        <input
                          type="text"
                          name="student_name"
                          value={formData.student_name}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Student's Full Name"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-group">
                        <input
                          type="text"
                          name="parent_name"
                          value={formData.parent_name}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Parent's Name"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Email Address"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Phone Number"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-group">
                        <select
                          name="grade"
                          value={formData.grade}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Grade</option>
                          <option value="Play Group">Play Group (2-3 Years)</option>
                          <option value="Nursery">Nursery (3-4 Years)</option>
                          <option value="LKG">LKG (4-5 Years)</option>
                          <option value="UKG">UKG (5-6 Years)</option>
                          <option value="1">Class 1</option>
                          <option value="2">Class 2</option>
                          <option value="3">Class 3</option>
                          <option value="4">Class 4</option>
                          <option value="5">Class 5</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-group">
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Your Message"
                          rows={4}
                          required
                        ></textarea>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <button type="submit" className="btn btn-primary w-100 py-2">
                        Submit Enquiry
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            
            {/* Map Column */}
            <div className="col-lg-6">
              <div className="map-container h-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3558.6284294333373!2d81.000227!3d26.883544!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399be2a7b02908f7%3A0x5f4209ecb746a560!2sNew%20ABC%20Montessori%20School!5e0!3m2!1sen!2sin!4v1766340808135!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '500px' }}
                  allowFullScreen={true}
                  loading="lazy"
                  title="School Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Second Row: Contact Information */}
          <div className="row">
            <div className="col-12">
              <div className="contact-info-container">
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="info-item">
                      <i className="fas fa-map-marker-alt"></i>
                      <div>
                        <h4>Our Location</h4>
                        <p>9/40, Sector 9, Indira Nagar, Lucknow</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item">
                      <i className="fas fa-phone"></i>
                      <div>
                        <h4>Phone Number</h4>
                        <p>+91-8400621111</p>
                        <p className='ms-2'> 0522-4750271</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item">
                      <i className="fas fa-envelope"></i>
                      <div>
                        <h4>Email Address</h4>
                        <p>newabcmontessori63@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;